import { useCallback, useEffect, useState } from 'react';
import { getCorpusRoles } from './api';
import {
  type CorpusStateSnapshot,
  type OnboardingProgress,
  type OnboardingStep,
  advanceOnboardingProgress,
  readOnboardingProgress,
  resolveOnboardingStep,
  skipOnboarding,
  writeOnboardingProgress,
} from './onboardingStep';

/**
 * Wires onboardingStep.ts's pure corpus-state -> step function to the ONE
 * real server-truth signal it needs: GET /corpus/roles' length (the same
 * route useCorpusCanvas already calls for the Refine tab). Deliberately does
 * NOT reuse useCorpusCanvas itself — that hook also loads achievements + drag
 * state, which this gate has no use for; a role-count-only read keeps
 * App.tsx's mount-time gate check cheap (one GET, no achievements payload).
 *
 * The corpus read only fires for a signed-in user (App.tsx gates this hook's
 * mount on useAuth().user being non-null — see App.tsx's own comment) — a
 * pre-signup stranger never issues a GET /corpus/roles at all, matching the
 * diagram's step 0/1 (landing + the demo card render with ZERO backend call).
 */
export function useOnboardingGate(enabled: boolean) {
  const [corpus, setCorpus] = useState<CorpusStateSnapshot>({ roleCount: null });
  const [progress, setProgress] = useState<OnboardingProgress>(() => readOnboardingProgress());

  const refreshCorpus = useCallback(async () => {
    if (!enabled) return;
    try {
      const roles = await getCorpusRoles();
      setCorpus({ roleCount: roles.length });
    } catch {
      // A failed read is genuinely "unknown," never "empty" — resolveOnboardingStep's
      // null-handling falls back to local progress rather than assuming roleCount:0
      // (which would incorrectly restart a returning user's demo/interview flow on a
      // transient network blip). Matches useCorpusCanvas/useJobsInbox's own "never
      // silently coerce a failure into a fabricated empty state" discipline.
      setCorpus((s) => s);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) refreshCorpus();
  }, [enabled, refreshCorpus]);

  const advance = useCallback((step: OnboardingStep) => {
    setProgress((p) => {
      const next = advanceOnboardingProgress(p, step);
      writeOnboardingProgress(next);
      return next;
    });
  }, []);

  const skip = useCallback(() => {
    setProgress((p) => {
      const next = skipOnboarding(p);
      writeOnboardingProgress(next);
      return next;
    });
  }, []);

  const step = resolveOnboardingStep(corpus, progress);

  return { step, corpus, progress, advance, skip, refreshCorpus };
}
